// @flow
import * as React from 'react';
import BoardPickerRowIcon from 'app/common/react/components/save/pin-to-board/BoardPickerPane/BoardPickerRowIcon';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import type { RepinBoardSection } from 'app/common/redux/reducers/sections';
import type { SaveStateType } from 'app/common/react/components/save/pin-to-board/PinToBoard/PinToBoard';
import type { SummaryBoard } from 'app/common/redux/reducers/board';
import { Box, Icon, Image, Spinner, Text } from 'gestalt';

type Props = {
  disabled: boolean,
  // true if we're currently in the section picker
  isWithinSectionPicker?: boolean,
  isSection: boolean,
  item: SummaryBoard | RepinBoardSection,
  isSelectMode?: boolean,
  saveState: ?SaveStateType,
  searchQuery: ?string,
  section_count?: number,
  selected: ?boolean,
  size: 'md' | 'lg',
};

export function getBoardLabel(searchQuery: ?string, name: string): any {
  const splitRegExp = searchQuery && searchQuery.length > 1 ? new RegExp(searchQuery, 'i') : '';
  const boardLabel =
    searchQuery && searchQuery.length > 1
      ? name.split(splitRegExp).reduce((prev, cur, index) => {
          if (prev.length > 0 && searchQuery && searchQuery.length) {
            const indexInString = name.toLowerCase().indexOf(searchQuery.toLowerCase());
            return prev.concat(
              <em
                key={index}
                style={{
                  borderBottom: '1px solid #333',
                  fontStyle: 'normal',
                  paddingBottom: '1px',
                }}
              >
                {name.substring(indexInString, indexInString + searchQuery.length)}
              </em>,
              cur,
            );
          }
          return [cur];
        }, [])
      : name;
  return boardLabel;
}

export function BoardPickerRow(props: Props) {
  const {
    disabled,
    item,
    isSection,
    isSelectMode,
    isWithinSectionPicker,
    saveState,
    searchQuery,
    selected,
    size = 'md',
  } = props;

  const board: ?SummaryBoard = isSection ? null : (item: any);
  const section: ?RepinBoardSection = isSection ? (item: any) : null;
  const name = (section && section.title) || (board && board.name) || '';
  let image = '';
  let isGroup = false;
  let privacy = '';
  let sectionCount = 0;
  if (board) {
    ({
      image_cover_url: image,
      is_collaborative: isGroup,
      privacy,
      section_count: sectionCount = 0,
    } = board);
  }
  const boardLabel = getBoardLabel(searchQuery, name);
  const isPrivate = privacy === CONST.BoardPrivacy.SECRET;
  const showSelectedState = !disabled && (selected || !!saveState);
  // we don't show sectionedBoards as different within the section picker view
  const hasSections = !isWithinSectionPicker && sectionCount > 0;

  const dataTestId = sectionCount > 0 ? 'boardWithSection' : 'boardWithoutSection';

  const saveOrSelect = isSelectMode
    ? i18n._('Select', 'Board select button label')
    : i18n._('Save', 'Pin save button label');

  return (
    <Box
      alignItems="center"
      color={showSelectedState ? 'lightGray' : 'white'}
      display="flex"
      paddingX={size === 'lg' ? 4 : 6}
      paddingY={2}
      data-test-id={dataTestId}
    >
      <Box
        height={size === 'lg' ? 40 : 36}
        width={size === 'lg' ? 40 : 36}
        color="lightGray"
        overflow="hidden"
        shape="rounded"
        marginRight={2}
        flex="none"
      >
        {image ? (
          <Image src={image} alt={name} fit="cover" naturalWidth={1} naturalHeight={1} />
        ) : (
          <span />
        )}
      </Box>
      <Box alignItems="center" justifyContent="between" display="flex" flex="grow">
        <Text
          color={disabled ? 'gray' : 'darkGray'}
          bold
          size={size === 'lg' ? 'xl' : 'md'}
          truncate
        >
          {boardLabel}
        </Text>
        {(hasSections || !showSelectedState) && (
          <Box alignItems="center" display="flex">
            {isPrivate && <BoardPickerRowIcon type="secret" />}
            {isGroup && <BoardPickerRowIcon type="group" />}
            {hasSections && <BoardPickerRowIcon type="sections" />}
          </Box>
        )}
        {!hasSections &&
          showSelectedState &&
          !saveState && (
            <Box
              alignItems="center"
              data-test-id="BoardPickerSaveButton"
              display="flex"
              flex="none"
              paddingX={3}
              paddingY={2}
              dangerouslySetInlineStyle={{
                __style: {
                  backgroundColor: '#e60023',
                  borderRadius: '4px',
                },
              }}
            >
              {!isSelectMode && (
                <Box
                  dangerouslySetInlineStyle={{
                    __style: {
                      marginRight: 3,
                      marginLeft: -6,
                    },
                  }}
                >
                  <Icon
                    accessibilityLabel="" // already has a label
                    color="white"
                    icon="pin"
                    inline
                    size={20}
                  />
                </Box>
              )}
              <Text bold color="white" size="sm" overflow="normal">
                {saveOrSelect}
              </Text>
            </Box>
          )}
        {!hasSections &&
          showSelectedState &&
          saveState === 'loading' && (
            <Box
              dangerouslySetInlineStyle={{
                __style: {
                  transform: 'scale(0.7)',
                },
              }}
              maxHeight={35}
            >
              <Spinner
                accessibilityLabel={i18n._(
                  'Saving',
                  'Accessibility label for saving a pin to a board',
                )}
                show
              />
            </Box>
          )}
      </Box>
    </Box>
  );
}

export default BoardPickerRow;
